import React, { useState, useEffect } from "react";
import { X, Upload, Globe, Plus, Trash2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Swal from "sweetalert2";
import toast from "react-hot-toast";
import axios from "axios";
import { uploadToCloudinary } from "../../../utils/cloudinary";
import { useClickOutside } from "../../Hooks/useClickOutside";

const AddOrEditBlogModal = ({ isOpen, onClose, onSuccess, blog }) => {
  const isEditing = Boolean(blog?._id);

  // Initialize form data for multilingual support
  const [formData, setFormData] = useState({
    title: { en: "", hi: "", es: "", zh: "" },
    content: { en: "", hi: "", es: "", zh: "" },
    author: "Admin",
    image: "",
  });

  const [imageFile, setImageFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeLanguage, setActiveLanguage] = useState("en");
  const [enableMultilingual, setEnableMultilingual] = useState(false);
  
  const modalRef = useClickOutside(() => onClose());

  // Initialize form with existing blog data
  useEffect(() => {
    if (isEditing && blog) {
      // Process multilingual fields
      const processField = (field) => {
        if (!field) return { en: "", hi: "", es: "", zh: "" };
        if (typeof field === "object") {
          return {
            en: field.en || "",
            hi: field.hi || "",
            es: field.es || "",
            zh: field.zh || "",
          };
        }
        // Convert old single-language blog to multilingual format
        return { en: field, hi: "", es: "", zh: "" };
      };

      const processedTitle = processField(blog.title);
      const processedContent = processField(blog.content);
      
      // Check if blog has multilingual data
      const hasMultipleLanguages = 
        blog.title && typeof blog.title === "object" ||
        blog.content && typeof blog.content === "object";
      
      setFormData({
        title: processedTitle,
        content: processedContent,
        author: blog.author || "Admin",
        image: blog.image || "",
      });
      
      setEnableMultilingual(hasMultipleLanguages);
    } else {
      // Reset for new blog
      setFormData({
        title: { en: "", hi: "", es: "", zh: "" },
        content: { en: "", hi: "", es: "", zh: "" },
        author: "Admin",
        image: "",
      });
      setEnableMultilingual(false);
      setActiveLanguage("en");
    }
    setImageFile(null);
  }, [blog, isEditing]);

  // Handle multilingual input changes
  const handleMultilingualChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: {
        ...prev[name],
        [activeLanguage]: value,
      },
    }));
  };

  // Handle single field changes
  const handleSingleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setLoading(true);
      toast.loading("Uploading image...");
      const uploaded = await uploadToCloudinary(file);
      setFormData(prev => ({ 
        ...prev, 
        image: uploaded.secure_url || uploaded.url 
      }));
      toast.dismiss();
      toast.success("Image uploaded successfully!");
    } catch (error) {
      toast.dismiss();
      toast.error("Failed to upload image");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate at least English content
    if (!formData.title.en.trim()) {
      toast.error("English title is required!");
      return;
    }
    if (!formData.content.en.trim()) {
      toast.error("English content is required!");
      return;
    }
    if (!formData.image) {
      toast.error("Blog image is required!");
      return;
    }

    try {
      setLoading(true);

      // Prepare payload based on multilingual setting
      const payload = {
        author: formData.author,
        image: formData.image,
        title: enableMultilingual 
          ? {
              en: formData.title.en.trim(),
              hi: formData.title.hi.trim() || formData.title.en.trim(),
              es: formData.title.es.trim() || formData.title.en.trim(),
              zh: formData.title.zh.trim() || formData.title.en.trim(),
            }
          : formData.title.en.trim(),
        content: enableMultilingual
          ? {
              en: formData.content.en.trim(),
              hi: formData.content.hi.trim() || formData.content.en.trim(),
              es: formData.content.es.trim() || formData.content.en.trim(),
              zh: formData.content.zh.trim() || formData.content.en.trim(),
            }
          : formData.content.en.trim(),
      };

      const API_BASE = process.env.REACT_APP_API_URL;

      if (isEditing) {
        await axios.put(`${API_BASE}/blog/edit-blog/${blog._id}`, payload);
        toast.success("Blog updated successfully!");
      } else {
        await axios.post(`${API_BASE}/blog/create-blog`, payload);
        toast.success("Blog created successfully!");
      }

      onSuccess();
      onClose();
    } catch (err) {
      console.error("Blog save error:", err);
      Swal.fire({
        icon: "error",
        title: "Something went wrong!",
        text: err.response?.data?.message || "Please try again later.",
      });
    } finally {
      setLoading(false);
    }
  };

  // Language tabs component
  const LanguageTabs = () => (
    <div className="flex space-x-2 mb-4">
      {["en", "hi", "es", "zh"].map((lang) => (
        <button
          key={lang}
          type="button"
          onClick={() => setActiveLanguage(lang)}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
            activeLanguage === lang
              ? "bg-blue-500 text-white"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
          }`}
        >
          {lang === "en" ? "English" : 
           lang === "hi" ? "हिंदी" : 
           lang === "es" ? "Español" : 
           "中文"}
        </button>
      ))}
    </div>
  );

  // Clear specific language content
  const clearLanguageContent = (lang) => {
    setFormData(prev => ({
      ...prev,
      title: {
        ...prev.title,
        [lang]: "",
      },
      content: {
        ...prev.content,
        [lang]: "",
      },
    }));
  };

  // Copy English content to other languages
  const copyEnglishToAll = () => {
    setFormData(prev => ({
      ...prev,
      title: {
        en: prev.title.en,
        hi: prev.title.hi || prev.title.en,
        es: prev.title.es || prev.title.en,
        zh: prev.title.zh || prev.title.en,
      },
      content: {
        en: prev.content.en,
        hi: prev.content.hi || prev.content.en,
        es: prev.content.es || prev.content.en,
        zh: prev.content.zh || prev.content.en,
      },
    }));
    toast.success("English content copied to all languages!");
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
            <div
              ref={modalRef}
              className="bg-[#ecf1f5] rounded-2xl shadow-2xl w-full max-w-2xl p-6 relative my-8"
            >
              <button
                onClick={onClose}
                className="absolute top-3 right-3 p-2 rounded-full hover:bg-gray-100 transition z-10"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>

              <h2 className="text-2xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                {isEditing ? "Edit Blog" : "Add New Blog"}
                {enableMultilingual && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-600 text-sm rounded-full">
                    <Globe className="w-4 h-4" />
                    Multilingual
                  </span>
                )}
              </h2>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Multilingual Toggle */}
                <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                  <div className="flex items-center gap-2">
                    <Globe className="w-5 h-5 text-gray-600" />
                    <div>
                      <p className="font-medium text-gray-700">Multilingual Support</p>
                      <p className="text-xs text-gray-500">
                        Enable to add content in multiple languages
                      </p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={enableMultilingual}
                      onChange={(e) => setEnableMultilingual(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
                  </label>
                </div>

                {/* Language Tabs (only when multilingual is enabled) */}
                {enableMultilingual && (
                  <div className="bg-white rounded-lg p-4 border">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-700">Language Content</span>
                        <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                          Currently editing: {activeLanguage === "en" ? "English" : 
                            activeLanguage === "hi" ? "Hindi" : 
                            activeLanguage === "es" ? "Spanish" : "Chinese"}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={copyEnglishToAll}
                        className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                      >
                        <Plus className="w-4 h-4" />
                        Copy English to all
                      </button>
                    </div>
                    <LanguageTabs />
                    
                    {/* Language Content Status */}
                    <div className="grid grid-cols-4 gap-2 mt-3">
                      {["en", "hi", "es", "zh"].map((lang) => {
                        const hasTitle = formData.title[lang]?.trim();
                        const hasContent = formData.content[lang]?.trim();
                        return (
                          <div
                            key={lang}
                            className={`p-2 rounded border text-center ${
                              lang === activeLanguage
                                ? "bg-blue-50 border-blue-300"
                                : "bg-gray-50"
                            }`}
                          >
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs font-medium">
                                {lang === "en" ? "EN" : 
                                 lang === "hi" ? "HI" : 
                                 lang === "es" ? "ES" : "ZH"}
                              </span>
                              {lang !== "en" && (hasTitle || hasContent) && (
                                <button
                                  type="button"
                                  onClick={() => clearLanguageContent(lang)}
                                  className="text-red-400 hover:text-red-600"
                                  title="Clear this language"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                            <div className="flex flex-col gap-1">
                              <span className={`text-xs ${hasTitle ? "text-green-600" : "text-gray-400"}`}>
                                Title: {hasTitle ? "✓" : "–"}
                              </span>
                              <span className={`text-xs ${hasContent ? "text-green-600" : "text-gray-400"}`}>
                                Content: {hasContent ? "✓" : "–"}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    
                    <p className="text-xs text-gray-500 mt-3">
                      <span className="font-medium">Note:</span> English is required. Other languages will auto-fill with English if left empty.
                    </p>
                  </div>
                )}

                {/* Image Upload */}
                <div className="bg-white rounded-lg p-4 border">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Blog Image <span className="text-red-500">*</span>
                  </label>
                  <div className="flex flex-col md:flex-row items-start gap-4">
                    <label className="relative block w-40 h-40 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 transition overflow-hidden bg-gray-50">
                      {formData.image ? (
                        <div className="relative w-full h-full">
                          <img
                            src={formData.image}
                            alt="Preview"
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-black/0 hover:bg-black/20 transition-all flex items-center justify-center">
                            <Upload className="w-6 h-6 text-white opacity-0 hover:opacity-100 transition" />
                          </div>
                        </div>
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center text-gray-400">
                          <Upload className="w-8 h-8 mb-2" />
                          <span className="text-sm">Click to upload</span>
                          <span className="text-xs text-gray-500">Recommended: 800x400px</span>
                        </div>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoUpload}
                        className="hidden"
                        disabled={loading}
                      />
                    </label>
                    <div className="flex-1">
                      <p className="text-sm text-gray-600 mb-2">
                        Upload a high-quality image for your blog post.
                      </p>
                      {formData.image && (
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, image: "" }))}
                          className="text-sm text-red-600 hover:text-red-800"
                        >
                          Remove image
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Author */}
                <div className="bg-white rounded-lg p-4 border">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Author
                  </label>
                  <input
                    type="text"
                    name="author"
                    value={formData.author}
                    onChange={handleSingleChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-400 outline-none"
                    placeholder="Enter author name"
                  />
                </div>

                {/* Title Field */}
                <div className="bg-white rounded-lg p-4 border">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title <span className="text-red-500">*</span>
                    {enableMultilingual && (
                      <span className="text-xs text-gray-500 ml-2">
                        ({activeLanguage === "en" ? "English" : 
                          activeLanguage === "hi" ? "Hindi" : 
                          activeLanguage === "es" ? "Spanish" : "Chinese"})
                      </span>
                    )}
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={enableMultilingual ? formData.title[activeLanguage] || "" : formData.title.en || ""}
                    onChange={enableMultilingual ? handleMultilingualChange : handleSingleChange}
                    required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-400 outline-none"
                    placeholder={
                      enableMultilingual
                        ? activeLanguage === "en"
                          ? "Enter blog title in English"
                          : activeLanguage === "hi"
                          ? "हिंदी में ब्लॉग शीर्षक दर्ज करें"
                          : activeLanguage === "es"
                          ? "Ingrese el título del blog en español"
                          : "输入中文博客标题"
                        : "Enter blog title"
                    }
                  />
                  {enableMultilingual && !formData.title.en.trim() && activeLanguage !== "en" && (
                    <p className="text-xs text-red-500 mt-1">
                      English title is required. Please fill in the English version first.
                    </p>
                  )}
                </div>

                {/* Content Field */}
                <div className="bg-white rounded-lg p-4 border">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Content <span className="text-red-500">*</span>
                    {enableMultilingual && (
                      <span className="text-xs text-gray-500 ml-2">
                        ({activeLanguage === "en" ? "English" : 
                          activeLanguage === "hi" ? "Hindi" : 
                          activeLanguage === "es" ? "Spanish" : "Chinese"})
                      </span>
                    )}
                  </label>
                  <textarea
                    name="content"
                    value={enableMultilingual ? formData.content[activeLanguage] || "" : formData.content.en || ""}
                    onChange={enableMultilingual ? handleMultilingualChange : handleSingleChange}
                    required
                    rows="6"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-400 outline-none resize-vertical"
                    placeholder={
                      enableMultilingual
                        ? activeLanguage === "en"
                          ? "Enter blog content in English..."
                          : activeLanguage === "hi"
                          ? "हिंदी में ब्लॉग सामग्री दर्ज करें..."
                          : activeLanguage === "es"
                          ? "Ingrese el contenido del blog en español..."
                          : "输入中文博客内容..."
                        : "Enter blog content..."
                    }
                  ></textarea>
                  {enableMultilingual && !formData.content.en.trim() && activeLanguage !== "en" && (
                    <p className="text-xs text-red-500 mt-1">
                      English content is required. Please fill in the English version first.
                    </p>
                  )}
                </div>

                {/* Preview Section */}
                <div className="bg-white rounded-lg p-4 border">
                  <h3 className="text-sm font-medium text-gray-700 mb-3">Preview</h3>
                  <div className="bg-gray-50 border rounded p-3 text-sm">
                    <p className="font-medium mb-1">Title ({enableMultilingual ? "English" : "Preview"}):</p>
                    <p className="text-gray-600 mb-2 line-clamp-2">
                      {formData.title.en || "(No title yet)"}
                    </p>
                    <p className="font-medium mb-1">Content Preview:</p>
                    <p className="text-gray-600 line-clamp-3">
                      {formData.content.en || "(No content yet)"}
                    </p>
                  </div>
                </div>

                {/* Buttons */}
                <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-5 py-2 rounded-lg bg-[#b07d17] text-white hover:bg-[#8c6312] transition disabled:opacity-50 flex items-center gap-2"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Saving...
                      </>
                    ) : isEditing ? (
                      "Update Blog"
                    ) : (
                      "Save Blog"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default AddOrEditBlogModal;








// import React, { useState, useEffect } from "react";
// import { X, Upload } from "lucide-react";
// import { motion, AnimatePresence } from "framer-motion";
// import Swal from "sweetalert2";
// import toast from "react-hot-toast";
// import axios from "axios";
// import { uploadToCloudinary } from "../../../utils/cloudinary";
// import { useClickOutside } from "../../Hooks/useClickOutside";

// const AddOrEditBlogModal = ({ isOpen, onClose, onSuccess, blog }) => {
//   const isEditing = Boolean(blog?._id);

//   const [formData, setFormData] = useState({
//     title: "",
//     author: "",
//     content: "",
//     image: "",
//   });

//   const [imageFile, setImageFile] = useState(null);
//   const [loading, setLoading] = useState(false);
//   const modalRef = useClickOutside(() => onClose());

//   useEffect(() => {
//     if (isEditing && blog) {
//       setFormData({
//         title: blog.title || "",
//         author: blog.author || "",
//         content: blog.content || "",
//         image: blog.image || "",
//       });
//     } else {
//       setFormData({ title: "", author: "", content: "", image: "" });
//     }
//   }, [blog, isEditing]);

//   const handleChange = (e) => {
//     const { name, value } = e.target;
//     setFormData((prev) => ({ ...prev, [name]: value }));
//   };

//   const handlePhotoUpload = (e) => {
//     const file = e.target.files[0];
//     if (file) {
//       setImageFile(file);
//       setFormData((prev) => ({ ...prev, image: URL.createObjectURL(file) }));
//     }
//   };

// const handleSubmit = async (e) => {
//   e.preventDefault();

//   try {
//     setLoading(true);

//     let imageUrl = formData.image;

//     // ✅ Only send image URL string to backend
//     if (imageFile) {
//       const uploaded = await uploadToCloudinary(imageFile);
//       imageUrl = uploaded.secure_url || uploaded.url;
//     }

//     const payload = {
//       title: formData.title,
//       content: formData.content,
//       author: formData.author,
//       image: imageUrl, // ✅ always a string now
//     };

//     const API_BASE = process.env.REACT_APP_API_URL;

//     if (isEditing) {
//       await axios.put(`${API_BASE}/blog/edit-blog/${blog._id}`, payload);
//       toast.success("Blog updated successfully!");
//     } else {
//       await axios.post(`${API_BASE}/blog/create-blog`, payload);
//       toast.success("Blog created successfully!");
//     }

//     onSuccess();
//     onClose();
//   } catch (err) {
//     console.error("Blog save error:", err);
//     Swal.fire({
//       icon: "error",
//       title: "Something went wrong!",
//       text: err.response?.data?.message || "Please try again later.",
//     });
//   } finally {
//     setLoading(false);
//   }
// };


//   return (
//     <AnimatePresence>
//       {isOpen && (
//         <>
//           <motion.div
//             className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
//             initial={{ opacity: 0 }}
//             animate={{ opacity: 1 }}
//             exit={{ opacity: 0 }}
//           />

//           <motion.div
//             className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
//             initial={{ scale: 0.9, opacity: 0 }}
//             animate={{ scale: 1, opacity: 1 }}
//             exit={{ scale: 0.9, opacity: 0 }}
//             transition={{ duration: 0.25 }}
//           >
//             <div
//               ref={modalRef}
//               className="bg-[#ecf1f5] rounded-2xl shadow-2xl w-full max-w-lg p-6 relative"
//             >
//               <button
//                 onClick={onClose}
//                 className="absolute top-3 right-3 p-2 rounded-full hover:bg-gray-100 transition"
//               >
//                 <X className="w-5 h-5 text-gray-500" />
//               </button>

//               <h2 className="text-2xl font-semibold text-gray-800 mb-4">
//                 {isEditing ? "Edit Blog" : "Add New Blog"}
//               </h2>

//               <form onSubmit={handleSubmit} className="space-y-4">
//                 {/* Image Upload */}
//                 <div>
//                   <label className="block text-sm font-medium text-gray-700 mb-2">
//                     Blog Image
//                   </label>
//                   <label className="relative block w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 transition overflow-hidden">
//                     {formData.image ? (
//                       <img
//                         src={formData.image}
//                         alt="Preview"
//                         className="w-full h-full object-cover"
//                       />
//                     ) : (
//                       <div className="w-full h-full flex flex-col items-center justify-center text-gray-400">
//                         <Upload className="w-6 h-6 mb-1" />
//                         <span className="text-xs">Click to upload</span>
//                       </div>
//                     )}
//                     <input
//                       type="file"
//                       accept="image/*"
//                       onChange={handlePhotoUpload}
//                       className="hidden"
//                     />
//                   </label>
//                 </div>

//                 {/* Title */}
//                 <div>
//                   <label className="block text-sm font-medium text-gray-700 mb-1">
//                     Title
//                   </label>
//                   <input
//                     type="text"
//                     name="title"
//                     value={formData.title}
//                     onChange={handleChange}
//                     required
//                     className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-400 outline-none"
//                   />
//                 </div>

//                 {/* Author */}
//                 <div>
//                   <label className="block text-sm font-medium text-gray-700 mb-1">
//                     Author
//                   </label>
//                   <input
//                     type="text"
//                     name="author"
//                     value={formData.author}
//                     onChange={handleChange}
//                     required
//                     className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-400 outline-none"
//                   />
//                 </div>

//                 {/* Content */}
//                 <div>
//                   <label className="block text-sm font-medium text-gray-700 mb-1">
//                     Content
//                   </label>
//                   <textarea
//                     name="content"
//                     value={formData.content}
//                     onChange={handleChange}
//                     required
//                     rows="5"
//                     className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-400 outline-none resize-none"
//                   ></textarea>
//                 </div>

//                 {/* Buttons */}
//                 <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
//                   <button
//                     type="button"
//                     onClick={onClose}
//                     className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
//                   >
//                     Cancel
//                   </button>
//                   <button
//                     type="submit"
//                     disabled={loading}
//                     className="px-5 py-2 rounded-lg bg-[#b07d17] text-white hover:bg-[#8c6312] transition disabled:opacity-50"
//                   >
//                     {loading
//                       ? "Saving..."
//                       : isEditing
//                       ? "Update Blog"
//                       : "Save Blog"}
//                   </button>
//                 </div>
//               </form>
//             </div>
//           </motion.div>
//         </>
//       )}
//     </AnimatePresence>
//   );
// };

// export default AddOrEditBlogModal;
